/**
 * Draggable activity card with parent-controlled child data, hierarchy depth/expansion, and edit
 * callback. Resolves bundle/project breadcrumbs through the workspace store. Shared readiness
 * checks gate immersion and retrospective logging; checking completion opens a session, while
 * reopening uses an optimistic API update with rollback on thrown errors.
 *
 * The card is memoized and reads no store slice that changes with other activities, so a board of
 * hundreds of cards re-renders only the card whose activity changed. It is a plain draggable, not
 * a sortable item: a sortable item re-renders whenever any item of its list moves.
 *
 * @module apps/web/src/components/blocks/TaskBlock
 */

import { useDraggable } from '@dnd-kit/core';
import { useTasksStore } from '@/stores/tasksStore';
import { api } from '@/lib/api';
import { ActivityDone, activityIsDone } from '@/components/activities/ActivityDone';
import { Checkbox } from '@/components/ui/checkbox';
import { TaskUI } from '@fluxability/types';
import { Badge } from '@/components/ui/badge';
import { getEnergyColor, getPriorityColor, formatDuration, formatTime } from '@/lib/utils';
import {
  GripVertical,
  ChevronDown,
  ChevronRight,
  Clock,
  Zap,
  Play,
  Layers,
  FolderKanban,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { ActivitySessionLogger } from '@/components/activities/ActivitySessionLogger';
import { memo, useState } from 'react';
import type { KanbanCardField } from '@/lib/kanbanApi';
import { formatMissingPlanningFields, getMissingPlanningFields } from '@/lib/activityReadiness';
import { toast } from '@/hooks/use-toast';

/** Activity-card data, optional hierarchy children, and parent-owned edit/expansion actions. */
interface TaskBlockProps {
  /** Activity displayed by this card. */
  task: TaskUI;
  /** Child activity records for hierarchy display; not React child elements. */
  children?: TaskUI[];
  /** Open the parent-owned editor for an activity ID. Keep it stable so the memoized card can skip renders. */
  onEdit: (taskId: string) => void;
  /** Toggle the parent-owned expansion state for an activity ID. */
  onToggleExpand?: (taskId: string) => void;
  /** Whether child activity cards are expanded. */
  isExpanded?: boolean;
  /** Hierarchy indentation level. */
  depth?: number;
  /** Optional board metadata selection; omitted preserves every existing card detail. */
  visibleFields?: KanbanCardField[];
  /** Compact content padding for saved boards; controls keep their normal sizes. */
  compact?: boolean;
  /** Non-status boards retain actions but do not imply status drag/drop. */
  dragDisabled?: boolean;
}

/**
 * Draggable activity card (drag ID = activity ID) with parent-controlled child data, hierarchy
 * depth/expansion, and edit callback. Resolves bundle/project breadcrumbs through the workspace
 * store. Shared readiness checks gate immersion and retrospective logging; checking completion
 * opens a session, while reopening uses an optimistic API update with rollback on thrown errors.
 * Wrapped in `memo` as {@link TaskBlock}.
 * @component TaskBlockCard
 */
function TaskBlockCard({
  task,
  children = [],
  onEdit,
  onToggleExpand,
  isExpanded = true,
  depth = 0,
  visibleFields,
  compact = false,
  dragDisabled = false,
}: TaskBlockProps) {
  const isDone = activityIsDone(task);
  const updateTask = useTasksStore(s => s.updateTask);
  const [isLoggingSession, setIsLoggingSession] = useState(false);
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const bundle = useWorkspaceStore(s => s.getBundle(task.bundleId));
  const project = useWorkspaceStore(s => s.getProject(bundle?.projectId || task.projectId));
  const missingPlanningFields = getMissingPlanningFields(task);
  const missingPlanningLabel = formatMissingPlanningFields(missingPlanningFields);
  const isPlanningComplete = missingPlanningFields.length === 0;
  const showPlanningRequiredToast = () => {
    toast({
      title: 'Fill planning details first',
      description: `Missing ${missingPlanningLabel}.`,
      variant: 'destructive',
    });
  };
  const handleComplete = (checked: boolean) => {
    if (checked) {
      if (!isPlanningComplete) {
        showPlanningRequiredToast();
        return;
      }
      startImmersionMode(task.id);
      return;
    }

    const reopenedCompletion = Math.min(99, Math.max(0, task.completionPercent ?? 99));
    updateTask(task.id, {
      status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
      completionPercent: reopenedCompletion,
    });
    api.tasks
      .update(task.id, {
        status: reopenedCompletion > 0 ? 'in_progress' : 'todo',
        completion_percent: reopenedCompletion,
        completed_at: null,
      })
      .catch(err => {
        console.error('Failed to reopen activity', err);
        updateTask(task.id, { status: task.status, completionPercent: task.completionPercent });
      });
  };
  // The board overlay draws the moving card; non-status grouping disables dragging.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: dragDisabled,
  });

  const hasChildren = children.length > 0;
  const energyColor = task.energyLevel ? getEnergyColor(task.energyLevel) : undefined;
  const priorityColor = getPriorityColor(task.priority);
  const scheduleLabel =
    task.scheduledStart && task.scheduledEnd
      ? `${formatTime(new Date(task.scheduledStart))} - ${formatTime(new Date(task.scheduledEnd))}`
      : null;
  // Logging past work needs no scheduled slot; the server accepts unscheduled activities.
  const canLogActivitySession = true;

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'border-border/80 bg-card/95 hover:border-ring/50 group mb-2 rounded-md border shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:shadow-sm',
        isDragging && 'opacity-50',
        depth > 0 && 'ml-6'
      )}
    >
      <div className={cn('flex items-start gap-2', compact ? 'p-2' : 'p-3')}>
        {/* Drag handle */}
        {!dragDisabled && (
          <button
            {...attributes}
            {...listeners}
            className="hover:bg-secondary cursor-grab touch-none rounded-md p-1 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
            aria-label="Drag handle"
          >
            <GripVertical className="text-muted-foreground h-5 w-5" />
          </button>
        )}

        {/* Expand/collapse button */}
        {hasChildren && onToggleExpand && (
          <button
            onClick={() => onToggleExpand(task.id)}
            className="flex-shrink-0"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronDown className="text-muted-foreground h-4 w-4" />
            ) : (
              <ChevronRight className="text-muted-foreground h-4 w-4" />
            )}
          </button>
        )}

        {/* Activity content */}
        <div className="pt-1" onPointerDown={e => e.stopPropagation()}>
          <Checkbox
            checked={isDone}
            onCheckedChange={handleComplete}
            aria-label={`${isDone ? 'Reopen' : 'Log progress for'} ${task.title}`}
          />
        </div>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onEdit(task.id)}>
          <div className="flex items-start gap-2">
            <h4
              className={cn(
                'line-clamp-2 text-sm font-medium leading-snug',
                isDone && 'text-muted-foreground line-through'
              )}
            >
              {task.title}
            </h4>
            <div className="flex flex-shrink-0 items-center gap-1">
              {isDone && <ActivityDone />}
              {/* Priority badge */}
              {(!visibleFields || visibleFields.includes('priority')) && (
                <Badge
                  variant="outline"
                  className="text-xs"
                  style={{ borderColor: priorityColor, color: priorityColor }}
                >
                  P{task.priority}
                </Badge>
              )}

              {/* Energy level */}
              {task.energyLevel && (
                <div
                  className="flex items-center gap-1 text-xs"
                  style={{ color: energyColor }}
                  title={`Energy: ${task.energyLevel}`}
                >
                  <Zap className="h-3 w-3" />
                  {task.energyLevel}
                </div>
              )}
            </div>

            {/* Immersion Mode Button */}
            <div className="ml-auto" onPointerDown={e => e.stopPropagation()}>
              {canLogActivitySession && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (!isPlanningComplete) {
                      showPlanningRequiredToast();
                      return;
                    }
                    setIsLoggingSession(true);
                  }}
                  disabled={!isPlanningComplete}
                  className="text-muted-foreground hover:text-primary hover:bg-secondary rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  title={
                    isPlanningComplete ? 'Log activity session' : `Missing ${missingPlanningLabel}`
                  }
                  aria-label={`Log session for ${task.title}`}
                >
                  <Clock className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (!isPlanningComplete) {
                    showPlanningRequiredToast();
                    return;
                  }
                  startImmersionMode(task.id);
                }}
                disabled={!isPlanningComplete}
                className="text-muted-foreground hover:text-primary hover:bg-secondary rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                title={
                  isPlanningComplete ? 'Start activity now' : `Missing ${missingPlanningLabel}`
                }
              >
                <Play className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{task.description}</p>
          )}

          {/* Metadata */}
          <div className="text-muted-foreground mt-2 grid gap-1.5 text-xs">
            {bundle && (!visibleFields || visibleFields.includes('bundle')) && (
              <div className="flex min-w-0 items-center gap-1.5">
                <Layers className="h-3 w-3 shrink-0" style={{ color: bundle.color }} />
                <span className="truncate">{bundle.name}</span>
              </div>
            )}
            {project && (
              <div className="flex min-w-0 items-center gap-1.5">
                <FolderKanban className="h-3 w-3 shrink-0" style={{ color: project.color }} />
                <span className="truncate">{project.name}</span>
              </div>
            )}
            {task.estimatedDuration && (!visibleFields || visibleFields.includes('duration')) && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(task.estimatedDuration)}
              </div>
            )}
            {scheduleLabel && (
              <div className="text-foreground flex items-center gap-1 font-medium">
                <Clock className="h-3 w-3" />
                Scheduled {scheduleLabel}
              </div>
            )}
            {task.deadline && (!visibleFields || visibleFields.includes('deadline')) && (
              <span>Due: {new Date(task.deadline).toLocaleDateString()}</span>
            )}
            {task.attributes && task.attributes.length > 0 && (
              <div className="flex gap-1">
                {task.attributes.map(attr => (
                  <Badge key={attr} variant="secondary" className="text-xs">
                    {attr}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Nested children */}
      {hasChildren && isExpanded && (
        <div className="pb-2 pl-4">
          {children.map(child => (
            <TaskBlock
              key={child.id}
              task={child}
              onEdit={onEdit}
              onToggleExpand={onToggleExpand}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
      {/* Mounted only while open: one closed dialog per card is measurable cost on a large board. */}
      {isLoggingSession && canLogActivitySession && (
        <ActivitySessionLogger
          task={task}
          open
          onClose={() => setIsLoggingSession(false)}
          onLogged={updatedTask => {
            updateTask(updatedTask.id, updatedTask);
            setIsLoggingSession(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Memoized {@link TaskBlockCard}: re-renders only when its activity record, children, callbacks
 * or hierarchy props change by identity.
 */
export const TaskBlock = memo(TaskBlockCard);
